/**
 * 채팅 화면 — F-2 채팅 코어의 소비자.
 *
 * 이 파일에는 계약이 없다. 파싱·판정·전송 조립은 전부 `src/chat/` 의 순수 함수·스토어에 있고,
 * 여기는 그 상태를 그린다. 화면이 계약을 조금씩 들고 있기 시작하면 다음 전환 때 또 F-0 을
 * 해야 한다. 상태 표기 역시 같은 규율이다 — pill·본문·드로어는 `deriveSurface` 하나를 읽는다.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ChangeEvent, FormEvent, KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { useSession } from '@/auth/SessionProvider'
import { JobProgressCard } from '@/jobs/JobProgressCard'
import { useAutoFollowUp } from '@/jobs/useAutoFollowUp'
import { useThreadJob } from '@/jobs/useThreadJob'
import { GateCard } from '@/chat/GateCard'
import { Markdown } from '@/chat/Markdown'
import { ToolCard } from '@/chat/ToolCard'
import { WarningsCard } from '@/chat/WarningsCard'
import { WorkDrawer } from '@/chat/WorkDrawer'
import { deriveSurface, pillOf } from '@/chat/surface'
import { fetchProjects } from '@/chat/projects'
import type { Project } from '@/chat/projects'
import { useChatStore } from '@/chat/store'
import { AppShell } from '@/ui/AppShell'
import { ArrowUp, Check, CircleAlert, Copy, FileText, Folder, Paperclip, X } from '@/ui/icons'

/** textarea 최대 높이 — CSS 의 max-height 와 같은 수여야 스크롤이 두 번 생기지 않는다. */
const TEXTAREA_MAX = 200
const TOAST_MS = 2_400

export default function ChatPage() {
  const { t } = useTranslation()
  const { session } = useSession()
  const { messages, gate, status, error, attachments, pings, threadId, projectId } =
    useChatStore()
  // 사이드바가 이미 받아 둔 목록을 같은 키로 재사용한다(재요청 없음 — 이름 하나 때문에
  // 프로젝트 상세를 따로 부르지 않는다).
  const projects = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: fetchProjects,
    enabled: Boolean(projectId),
    staleTime: 10_000,
  })
  const { send, respondToGate, attach, removeAttachment, stop, restore } = useChatStore()
  // 저작 job 은 **서버 데이터**라 Query 가 본다(실행 상태와 축이 다르다 — F-2 3분할).
  const { data: jobData, refetch: refetchJob } = useThreadJob(threadId)
  const job = jobData?.job ?? null
  const warnings = jobData?.warnings ?? []
  // 완료되면 에이전트가 이어 말한다(B §7 완화 설계 — B-6 블라인드 비교의 측정 대상).
  useAutoFollowUp(job)

  const [draft, setDraft] = useState('')
  const [uploadError, setUploadError] = useState('')
  const [uploading, setUploading] = useState(0)
  const [dropping, setDropping] = useState(false)
  const [toast, setToast] = useState('')

  const bottom = useRef<HTMLDivElement>(null)
  const chatRef = useRef<HTMLElement>(null)
  const composerRef = useRef<HTMLFormElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const toastTimer = useRef<number | undefined>(undefined)

  const streaming = status === 'streaming'
  const surface = deriveSurface(job, status)
  const pill = pillOf(surface)

  const showToast = useCallback((message: string) => {
    setToast(message)
    window.clearTimeout(toastTimer.current)
    toastTimer.current = window.setTimeout(() => setToast(''), TOAST_MS)
  }, [])

  useEffect(() => () => window.clearTimeout(toastTimer.current), [])

  // 새로고침 복원(U2) — 마지막 대화의 이력을 서버에서 되불러온다. 1회만.
  useEffect(() => {
    void restore()
  }, [restore])

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, gate, status])

  /* 컴포저 실측 높이 → 대화 하단 여백. textarea 가 200px 까지 자라면 고정 여백으로는
     마지막 메시지가 가린다 — 가려진 답을 사용자는 "안 왔다"로 읽는다. */
  useEffect(() => {
    const composer = composerRef.current
    const chat = chatRef.current
    if (!composer || !chat) return
    const sync = () => chat.style.setProperty('--composer-h', `${composer.offsetHeight}px`)
    sync()
    const observer = new ResizeObserver(sync)
    observer.observe(composer)
    return () => observer.disconnect()
  }, [])

  function resetTextareaHeight() {
    if (textareaRef.current) textareaRef.current.style.height = 'auto'
  }

  async function onSubmit(event?: FormEvent) {
    event?.preventDefault()
    const text = draft.trim()
    if (!text || streaming) return
    setDraft('')
    resetTextareaHeight()
    await send(text)
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      void onSubmit()
    }
  }

  /**
   * 여러 파일 첨부 — **한 번에 여러 개를 고를 수 있어야 한다**(구 앱 패리티).
   *
   * 백엔드 `/uploads` 는 한 요청에 한 파일이라 순차로 올린다. 한 건이 실패해도 **나머지는
   * 계속 올린다** — 5개 중 1개가 형식 위반이라고 나머지 4개를 버리면 사용자가 다시 다 골라야 한다.
   * 실패는 **파일명과 함께** 알린다(무엇이 문제인지 모르면 고칠 수 없다).
   */
  async function uploadAll(files: File[]) {
    if (files.length === 0) return
    setUploadError('')
    setUploading(files.length)
    const failures: string[] = []
    for (const file of files) {
      try {
        await attach(file)
      } catch (caught) {
        failures.push(
          `${file.name}: ${caught instanceof Error ? caught.message : t('chat.attach_failed')}`,
        )
      } finally {
        setUploading((count) => count - 1)
      }
    }
    if (failures.length) setUploadError(failures.join('\n'))
  }

  async function onPickFile(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? [])
    event.target.value = '' // 같은 파일 재선택 허용
    await uploadAll(files)
  }

  function copyMessage(text: string) {
    // 권한이 거부돼도 성공 토스트를 띄우지 않는다 — 복사됐다고 믿고 붙여넣으면 빈 값이 간다.
    const pending = navigator.clipboard?.writeText(text)
    if (!pending) {
      showToast(t('chat.copy_unsupported'))
      return
    }
    pending.then(() => showToast(t('chat.copied'))).catch(() => showToast(t('chat.copy_failed')))
  }

  const who = session.user?.login_id ?? ''
  const title = messages.length > 0 ? t('chat.thread') : t('nav.new_chat')
  /* 이 대화가 어느 프로젝트에 속하는지 상단바에 적는다(F-6). 없으면 아무것도 그리지 않는다.
     적어야 하는 이유: 프로젝트 지침이 이 대화의 답을 바꾸는데, 어느 지침이 걸려 있는지 화면에
     흔적이 없으면 사용자는 "왜 이렇게 답하지?"를 추측하게 된다. */
  const projectName = projects.data?.find((p) => p.project_id === projectId)?.name
  /* 인사말은 **정말 빈 대화**에서만. 이력이 비어도 진행 중인 저작·게이트가 있을 수 있는데
     (새로고침 복원은 threadId 를 살린다), 그때 인사말을 띄우면 돌아가는 작업이 사라진다. */
  const hasContent = messages.length > 0 || Boolean(job) || Boolean(gate) || status === 'error'

  return (
    <AppShell
      title={title}
      pill={
        <>
          {projectName && (
            <Link className="pill link" to={`/projects/${projectId}`} title={projectName}>
              <Folder size={12} />
              {projectName}
            </Link>
          )}
          {pill && (
            <span className={`pill ${pill.tone}`}>
              {pill.pulse && <span className="pulse" aria-hidden="true" />}
              {t(pill.key)}
            </span>
          )}
        </>
      }
      drawer={<WorkDrawer threadId={threadId} job={job} surface={surface} />}
    >
      <section className="chat" ref={chatRef}>
        {!hasContent ? (
          <div className="empty">
            <h1 className="greet">{who ? t('chat.greet_named', { name: who }) : t('chat.greet')}</h1>
            <p className="greet-sub">{t('chat.empty')}</p>
          </div>
        ) : (
          <div className="thread">
            {messages.map((message) =>
              message.role === 'user' ? (
                <article key={message.id} className="msg user">
                  <div className="bubble">{message.text}</div>
                </article>
              ) : (
                <article key={message.id} className="msg ai">
                  {message.parts.map((part, index) =>
                    part.kind === 'text' ? (
                      <Markdown key={`${message.id}-t${index}`} text={part.text} />
                    ) : (
                      <ToolCard key={`${message.id}-${part.callId}`} part={part} />
                    ),
                  )}
                  <div className="msg-tools">
                    <button
                      type="button"
                      onClick={() =>
                        copyMessage(
                          message.parts
                            .filter((part) => part.kind === 'text')
                            .map((part) => part.text)
                            .join('\n\n'),
                        )
                      }
                      aria-label={t('chat.copy')}
                      title={t('chat.copy')}
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                </article>
              ),
            )}

            {/* 경고는 산출물과 **나란히** 산다 — 완성도가 진행의 전제조건이 아니다(BR-501). */}
            <WarningsCard warnings={warnings} />

            {job && <JobProgressCard job={job} onChanged={() => void refetchJob()} />}

            {gate && (
              <GateCard gate={gate} busy={streaming} onDecide={(d) => void respondToGate(d)} />
            )}

            {streaming && (
              // 하트비트(ping)를 받은 사실을 표시에 쓴다 — 무산출 구간에서도 "멈춘 것"이 아님을
              // 사용자가 알 수 있어야 한다(장주기 도구는 8~9분간 산출이 없다).
              <p className="thinking">
                <span className="spinner" aria-hidden="true" />
                {t('chat.thinking')}
                {pings > 0 && ` ${t('chat.in_progress')}`}
                <button type="button" className="link" onClick={stop}>
                  {t('chat.stop')}
                </button>
              </p>
            )}

            {/* 시스템 오류 = danger **외곽선**(게이트의 채움과 형태로 구분 · P3′) */}
            {status === 'error' && error && (
              <section className="card error">
                <div className="card-head">
                  <CircleAlert size={17} className="err-ic" />
                  <span className="card-title">{t('chat.error_title')}</span>
                </div>
                <p className="err-sub">{error}</p>
                <p className="err-sub">{t('chat.error_preserved')}</p>
              </section>
            )}

            <div ref={bottom} />
          </div>
        )}
      </section>

      <div className="composer-wrap">
        <form
          className={`composer ${dropping ? 'dropping' : ''}`}
          ref={composerRef}
          onSubmit={(event) => void onSubmit(event)}
          onDragOver={(event) => {
            event.preventDefault()
            setDropping(true)
          }}
          onDragLeave={() => setDropping(false)}
          onDrop={(event) => {
            event.preventDefault()
            setDropping(false)
            void uploadAll(Array.from(event.dataTransfer.files ?? []))
          }}
        >
          {attachments.length > 0 && (
            <div className="composer-attaches">
              {attachments.map((file) => (
                <span className="composer-attach" key={file.file_ref}>
                  <FileText size={13} />
                  <span>{file.filename}</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(file.file_ref)}
                    aria-label={`${file.filename} ${t('chat.attach_remove')}`}
                  >
                    <X size={13} />
                  </button>
                </span>
              ))}
            </div>
          )}
          {uploading > 0 && (
            <p className="composer-status">
              {t('chat.attach_uploading')} ({uploading})
            </p>
          )}
          {uploadError && <p className="composer-status bad">{uploadError}</p>}

          <textarea
            ref={textareaRef}
            rows={1}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value)
              event.target.style.height = 'auto'
              event.target.style.height = `${Math.min(event.target.scrollHeight, TEXTAREA_MAX)}px`
            }}
            onKeyDown={onKeyDown}
            placeholder={t('chat.placeholder')}
            aria-label={t('chat.input_label')}
          />

          <div className="composer-row">
            <label className="icon-btn" title={t('chat.attach')}>
              <span className="sr-only">{t('chat.attach')}</span>
              <Paperclip size={17} />
              <input
                type="file"
                multiple
                accept=".pdf,.docx,.xlsx,.pptx,.hwp,.hwpx,.txt,.md,.csv"
                className="hidden"
                onChange={(event) => void onPickFile(event)}
              />
            </label>
            <span className="composer-hint-inline">{t('chat.enter_hint')}</span>
            <button
              type="submit"
              className="send"
              disabled={streaming || !draft.trim()}
              aria-label={t('chat.send')}
              title={t('chat.send')}
            >
              <ArrowUp size={16} />
            </button>
          </div>
        </form>
        <p className="hint">{t('chat.grounding_hint')}</p>
      </div>

      {toast && (
        <div className="toast" role="status">
          <Check size={14} />
          {toast}
        </div>
      )}
    </AppShell>
  )
}
